import React, { useState } from 'react';
import { createSearchParams, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvProgressCircle,
  Size,
} from '@salvapp/ui-components';
import { faCopy, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import SalvPage from '../../../common/SalvPage';
import { InternalNavigation } from '../../../navigation/helper';
import { useQueryScreeningFlow } from './screeningFlowActions';
import { useCreateDraftForFlow, useQueryFlowDrafts } from '../../fields/draft/screeningFlowDraftActions';
import { useQuerySegments } from '../segments/segmentActions';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';
import { Flow } from './Flow';
import { EmptySpaceFiller } from './ScreeningFlows';
import { getScreeningFlowDisplayId } from './helper';
import { applyFlowFilters, emptyFlowFilters, FlowFilters, ScreeningFlowFilters } from './ScreeningFlowFilters';
import { InternalApiScreeningFlow } from '../../../api-internal';
import { SearchParamKeys } from '../../../common/searchParamKeys';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { useMe } from '../../../organisation/useMe';
import { showFeedback } from '../../../feedback/feedbackReducer';
import './ScreeningFlows.scss';

const FlowGroupDetail = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { features, isLoading: isMeLoading } = useMe();
  const isFlowDraftsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SCREENING_FLOW_DRAFTS, features);
  const { mutateAsync: createDraftForFlow } = useCreateDraftForFlow(flowId ?? '');

  const {
    data: drafts,
    isPending: isDraftsPending,
    isError: isDraftsError,
  } = useQueryFlowDrafts(flowId, isFlowDraftsEnabled);

  const {
    data: activeFlow,
    isPending: isActiveFlowPending,
    isError: isActiveFlowError,
  } = useQueryScreeningFlow(flowId);
  const { data: allSegments, isPending: isSegmentPending, isError: isSegmentError } = useQuerySegments();

  const [filters, setFilters] = useState<FlowFilters>(emptyFlowFilters);

  if (isMeLoading) {
    return <SalvProgressCircle ariaLabel={'Loading flow'} />;
  }

  if (!isFlowDraftsEnabled) {
    return <DataLoadingErrorFallback message={'Screening flow drafts are not enabled for your organisation'} />;
  }

  if (isDraftsError || isActiveFlowError || isSegmentError) {
    return <DataLoadingErrorFallback message={'Could not load flow'} />;
  } else if (isDraftsPending || isActiveFlowPending || isSegmentPending) {
    return <SalvProgressCircle ariaLabel={'Loading flow'} />;
  }

  if (activeFlow === null && drafts.length === 0) {
    return <DataLoadingErrorFallback message={'Flow not found'} />;
  }

  const isPublished = !!activeFlow;
  const firstFlow = isPublished ? activeFlow : drafts[0];

  const entityType = firstFlow.entityType;
  const segments = allSegments.filter((segment) => segment.entityType === entityType);

  const goToWizard = ({ flow, isDraft }: { flow?: InternalApiScreeningFlow; isDraft: boolean }) => {
    navigate(
      {
        pathname: InternalNavigation.screeningFlowWizard,
        search: createSearchParams({
          [SearchParamKeys.entityType]: entityType,
        }).toString(),
      },
      { state: { flow, isDraft } }
    );
  };

  const allGroupFlows = [...(activeFlow ? [activeFlow] : []), ...drafts.map((draft) => draft.snapshot)];
  const visibleActiveFlow = activeFlow && applyFlowFilters([activeFlow], filters).length > 0 ? activeFlow : undefined;
  const visibleDrafts = drafts.filter((draft) => applyFlowFilters([draft.snapshot], filters).length > 0);
  const totalRows = (visibleActiveFlow ? 1 : 0) + visibleDrafts.length;
  let rowCounter = 0;

  return (
    <SalvPage
      title={getScreeningFlowDisplayId(flowId!)}
      className='screening-flows-container'
      breadcrumb={{
        title: 'Screening flows',
        to: {
          pathname: InternalNavigation.screeningFlows,
          search: createSearchParams({ [SearchParamKeys.entityType]: entityType }).toString(),
        },
      }}
      actions={
        activeFlow ? (
          <>
            <SalvButton
              label='Duplicate as draft'
              iconLeft={faCopy}
              variant='secondary'
              size={Size.SMALL}
              onClick={async () => {
                try {
                  await createDraftForFlow({ entityType, snapshot: activeFlow });
                  dispatch(showFeedback({ message: 'Draft created', success: true }));
                } catch {
                  dispatch(showFeedback({ message: 'Failed to create draft', success: false }));
                }
              }}
            />
            <SalvButton
              label='Edit'
              iconLeft={faPenToSquare}
              variant='primary'
              size={Size.SMALL}
              onClick={() => goToWizard({ flow: activeFlow, isDraft: false })}
              className={'m-l-8'}
            />
          </>
        ) : undefined
      }
    >
      {!isPublished && (
        <SalvBanner
          type={SalvBannerType.WARNING}
          accent={SalvBannerAccent.LIGHT}
          message={
            'This flow has no active version yet. Drafts run in isolation against historical data and ' +
            'never touch live traffic until promoted.'
          }
        />
      )}
      <div className='screening-flows' style={{ '--rows-count': totalRows } as React.CSSProperties}>
        <ScreeningFlowFilters flows={allGroupFlows} segments={segments} value={filters} onChange={setFilters} />
        {visibleActiveFlow && (
          <Flow rowNr={++rowCounter} flow={visibleActiveFlow} segments={segments} status='active' showButtonsAndId />
        )}
        {visibleDrafts.map((draft) => (
          <Flow
            key={`draft-${draft.id}`}
            rowNr={++rowCounter}
            flow={draft.snapshot}
            segments={segments}
            status='draft'
            showButtonsAndId
            draft={draft}
          />
        ))}
        <EmptySpaceFiller />
      </div>
    </SalvPage>
  );
};

export default FlowGroupDetail;
